import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Film, Building2, Sun, Calendar, BarChart3, DollarSign, CircleDollarSign, TrendingUp, TrendingDown, Minus, Truck, PieChart, ChevronDown, ChevronRight, Zap, Unplug, Leaf, Cog, Battery } from 'lucide-react';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { ENERGY_CONVERSION, ENERGY_RATES } from '@/lib/energyConstants';
import { currentSupabaseEnv } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { GroupBarChart } from './GroupBarChart';
import { StatsSummaryCard } from './StatsSummaryCard';
import { GridRevenueCard } from './GridRevenueCard';
import { EnergyMixDonut } from './EnergyMixDonut';
import { SummaryReportView } from './SummaryReportView';
import { GroupBadgeList } from './GroupBadgeList';
import { StationMetricsTable } from './StationMetricsTable';
import { useStationMetrics } from '@/hooks/useStationMetrics';
import { useEnergyTimeSeries } from '@/hooks/useEnergyTimeSeries';
import { useGroupEffectiveRate } from '@/hooks/useEffectiveRate';
import { useFilters } from '@/contexts/FilterContext';
import {
  GroupAnalyticsData,
  GroupAnalyticsFilters,
  StationMetricsTableData,
  GroupTimeSeriesPoint,
  TimeGranularity,
} from '@/types/reports';
import { Group, Company } from '@/types';
import { cn } from '@/lib/utils';

/**
 * Fill in missing periods with 0 values for the full date range
 * This ensures the chart shows the complete group period, not just dates with data
 */
function fillMissingPeriods(
  data: GroupTimeSeriesPoint[],
  startDate: Date | null,
  endDate: Date | null,
  granularity: TimeGranularity
): GroupTimeSeriesPoint[] {
  if (!startDate || !endDate) return data;

  const dataMap = new Map(data.map(d => [d.date, d]));
  const result: GroupTimeSeriesPoint[] = [];

  const current = new Date(startDate);
  current.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);

  // Helper to format period label
  const formatPeriodLabel = (date: Date, gran: TimeGranularity): string => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    if (gran === 'daily') {
      return `${months[date.getMonth()]} ${date.getDate()}`;
    } else if (gran === 'weekly') {
      // Get week number of the year
      const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
      const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
      const weekNum = Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
      return `Week ${weekNum}`;
    } else {
      return months[date.getMonth()];
    }
  };

  // Helper to get period start date as ISO string
  const getPeriodKey = (date: Date, gran: TimeGranularity): string => {
    if (gran === 'weekly') {
      // Get Monday of the week
      const day = date.getDay();
      const diff = date.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(date);
      monday.setDate(diff);
      return monday.toISOString().split('T')[0];
    } else if (gran === 'monthly') {
      // First day of month
      return new Date(date.getFullYear(), date.getMonth(), 1).toISOString().split('T')[0];
    }
    return date.toISOString().split('T')[0];
  };

  // Track which periods we've added
  const addedPeriods = new Set<string>();

  while (current <= end) {
    const periodKey = getPeriodKey(current, granularity);

    // Skip if we've already added this period (for weekly/monthly)
    if (addedPeriods.has(periodKey)) {
      // Advance to next day
      current.setDate(current.getDate() + 1);
      continue;
    }

    addedPeriods.add(periodKey);

    // Check if we have data for this period
    const existingData = dataMap.get(periodKey);

    if (existingData) {
      result.push(existingData);
    } else {
      // Create empty period
      result.push({
        date: periodKey,
        periodLabel: formatPeriodLabel(current, granularity),
        solarKwh: 0,
        gridKwh: 0,
        gridExportKwh: 0,
        generatorKwh: 0,
        offGridPercent: 0,
        co2Reduced: 0,
        costSaved: 0,
        gridSellRevenue: 0,
        unitsActive: 0,
      });
    }

    // Advance based on granularity
    if (granularity === 'daily') {
      current.setDate(current.getDate() + 1);
    } else if (granularity === 'weekly') {
      current.setDate(current.getDate() + 7);
    } else {
      current.setMonth(current.getMonth() + 1);
    }
  }

  return result;
}

const DEFAULT_LOGO = '/assets/ht-logo.png';

interface GroupAnalyticsPanelProps {
  groups: Group[];
  filters: GroupAnalyticsFilters;
  showCharts?: boolean;
  showStats?: boolean;
  // Date range from parent filters
  dateRange?: { start: Date | null; end: Date | null };
  // Selected company for logo display
  selectedCompany?: Company | null;
  // Dynamic label for group (Production vs Group based on industry)
  groupLabel?: string;
  // Whether selected groups are film industry (for counting groups)
  isFilmIndustry?: boolean;
  // Station metrics data from parent (to avoid duplicate API calls)
  stationMetricsData?: StationMetricsTableData | null;
  isLoadingMetrics?: boolean;
  // Unit count computed from units data (same source as dropdowns) - authoritative for display
  unitCount?: number;
  // Whether the filter context is still loading (prevents text flicker)
  isContextLoading?: boolean;
  // Additional className for layout control (e.g., flex-1)
  className?: string;
  // Whether no models are selected (show empty state for all sections)
  noModelsSelected?: boolean;
  // Deployment history for chart annotations (internal users only)
  deployments?: import('@/types/history').DeploymentPeriod[];
  // Whether to show deployment annotations on charts
  showDeploymentAnnotations?: boolean;
  /** Only these units (Reports → Unit Status click-to-focus); null = no unit filter */
  unitIds?: string[] | null;
}

interface KpiCardProps {
  icon: React.ElementType;
  label: string;
  value: string | number;
  delta?: number;
  iconColor?: string;
  subtitle?: string;
}

function KpiCard({ icon: Icon, label, value, delta, iconColor = '#8FB560', subtitle }: KpiCardProps) {
  const DeltaIcon = delta !== undefined && delta > 0 ? TrendingUp : delta !== undefined && delta < 0 ? TrendingDown : Minus;
  const deltaColor = delta !== undefined && delta > 0 ? 'text-[#8FB560]' : delta !== undefined && delta < 0 ? 'text-amber-500' : 'text-muted-foreground';
  const deltaBgColor = delta !== undefined && delta > 0 ? 'bg-[#8FB560]/10' : delta !== undefined && delta < 0 ? 'bg-amber-500/10' : 'bg-muted/50';

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        <div className="flex items-stretch min-h-[72px]">
          {/* Icon Column - Visual Anchor */}
          <div
            className="flex items-center justify-center w-14 shrink-0"
            style={{ backgroundColor: `${iconColor}15` }}
          >
            <Icon className="h-6 w-6" style={{ color: iconColor }} />
          </div>

          {/* Content Column */}
          <div className="flex-1 p-2 flex flex-col justify-center min-w-0 relative">
            {/* Delta chip - Top right */}
            {delta !== undefined && (
              <span className={cn(
                "absolute top-1.5 right-1.5 text-[10px] font-semibold tabular-nums inline-flex items-center gap-0.5 px-1 py-0.5 rounded-full",
                deltaColor,
                deltaBgColor
              )}>
                <DeltaIcon className="h-2.5 w-2.5" />
                {delta > 0 ? '+' : ''}{delta.toFixed(1)}%
              </span>
            )}

            {/* Label - Left aligned */}
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
              {label}
            </span>

            {/* Value - Centered */}
            <div className="flex items-baseline justify-center mt-0.5">
              <span className="text-lg font-bold tabular-nums">{value}</span>
            </div>

            {/* Subtitle/Context */}
            {subtitle && (
              <span className="text-[10px] text-muted-foreground/70 mt-0.5 text-center">
                {subtitle}
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function formatDateRange(start: string, end: string, daysInPeriod?: number): string {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const formatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  // Use provided daysInPeriod if available, otherwise calculate (inclusive)
  const days = daysInPeriod ?? (Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1);

  return `${formatter.format(startDate)} - ${formatter.format(endDate)} (${days} days)`;
}

// Skeleton components for zero layout shift
function KpiCardSkeleton({ icon: Icon, iconColor = '#6b7280' }: { icon: React.ElementType; iconColor?: string }) {
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        <div className="flex items-stretch min-h-[72px]">
          <div
            className="flex items-center justify-center w-16 shrink-0 opacity-30"
            style={{ backgroundColor: `${iconColor}15` }}
          >
            <Icon className="h-7 w-7" style={{ color: iconColor }} />
          </div>
          <div className="flex-1 p-3 flex flex-col justify-center min-w-0">
            <Skeleton className="h-3 w-16 mb-2" />
            <Skeleton className="h-6 w-20" />
            <Skeleton className="h-2.5 w-24 mt-1.5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ChartSkeleton({ showEmptyMessage = false, groupLabel = 'Production', isNoData = false, noModelsSelected = false }: { showEmptyMessage?: boolean; groupLabel?: string; isNoData?: boolean; noModelsSelected?: boolean }) {
  // Determine the message to show
  const getEmptyMessage = () => {
    if (noModelsSelected) {
      return {
        title: 'No Models Selected',
        subtitle: 'Select at least one model from the filters to view data',
      };
    }
    if (isNoData) {
      return {
        title: 'No Data Available',
        subtitle: 'No energy data found for the selected period and filters',
      };
    }
    return {
      title: `No ${groupLabel} Selected`,
      subtitle: `Select a ${groupLabel.toLowerCase()} from the dropdown to get started`,
    };
  };

  const message = getEmptyMessage();

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-[#8FB560] opacity-30" />
          <Skeleton className="h-3 w-40" />
        </CardTitle>
      </CardHeader>
      <CardContent className="px-6 pb-2">
        <div className="h-[280px] relative">
          {showEmptyMessage ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              {noModelsSelected ? (
                <Truck className="h-12 w-12 text-muted-foreground mb-4" />
              ) : groupLabel === 'Production' ? (
                <Film className="h-12 w-12 text-muted-foreground mb-4" />
              ) : (
                <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
              )}
              <p className="text-lg text-muted-foreground font-medium mb-1">
                {message.title}
              </p>
              <p className="text-base text-muted-foreground/70 text-center">
                {message.subtitle}
              </p>
            </div>
          ) : (
            <div className="h-full flex flex-col justify-end gap-2 pb-6">
              <div className="flex items-end gap-3 h-full px-4">
                {[65, 45, 80, 55, 70, 40, 75, 60, 85, 50, 65, 55].map((h, i) => (
                  <Skeleton key={i} className="flex-1" style={{ height: `${h}%` }} />
                ))}
              </div>
              <div className="flex justify-between px-4">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Skeleton key={i} className="h-3 w-8" />
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function StatsCardSkeleton() {
  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
            <Leaf className="h-4 w-4 text-[#8FB560] opacity-30" />
            <Skeleton className="h-3 w-32" />
          </CardTitle>
          <Skeleton className="h-3 w-8" />
        </div>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col justify-between">
        {/* Environmental Impact section */}
        <div className="space-y-0 divide-y divide-border/50">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center justify-between py-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
        {/* Off-Grid Performance section */}
        <div className="pt-4 mt-4 border-t border-border">
          <Skeleton className="h-3 w-32 mb-2" />
          <div className="space-y-0 divide-y divide-border/50">
            {[1, 2].map((i) => (
              <div key={i} className="flex items-center justify-between py-2">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-4 w-14" />
              </div>
            ))}
          </div>
        </div>
        {/* vs Prior Period section */}
        <div className="pt-4 mt-4 border-t border-border">
          <Skeleton className="h-3 w-24 mb-2" />
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-1">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-3 w-16" />
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DonutSkeleton() {
  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
          <PieChart className="h-4 w-4 text-[#8FB560] opacity-30" />
          <Skeleton className="h-3 w-28" />
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex items-center justify-center">
        <div className="relative">
          <Skeleton className="h-32 w-32 rounded-full" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-16 w-16 rounded-full bg-card" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function GridRevenueCardSkeleton() {
  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-2 flex-shrink-0">
        <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
          <CircleDollarSign className="h-4 w-4 text-[#8FB560] opacity-30" />
          <Skeleton className="h-3 w-24" />
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col">
        {/* Hero placeholder */}
        <div className="text-center pb-3">
          <Skeleton className="h-9 w-24 mx-auto" />
          <Skeleton className="h-3 w-20 mx-auto mt-1" />
        </div>
        {/* Treemap placeholder */}
        <div className="py-3 flex-1 min-h-[100px]">
          <Skeleton className="h-full w-full rounded-lg" />
        </div>
        {/* Bottom stats placeholder */}
        <div className="mt-auto pt-3 border-t border-border/50">
          <div className="grid grid-cols-3 gap-2 text-center">
            {[1, 2, 3].map((i) => (
              <div key={i}>
                <Skeleton className="h-3 w-12 mx-auto mb-1" />
                <Skeleton className="h-4 w-16 mx-auto" />
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Summary skeleton - matches SummaryReportView layout
function SummarySkeleton({ showEmptyMessage = false, groupLabel = 'Production', isNoData = false, noModelsSelected = false }: { showEmptyMessage?: boolean; groupLabel?: string; isNoData?: boolean; noModelsSelected?: boolean }) {
  // Determine the message to show
  const getEmptyMessage = () => {
    if (noModelsSelected) {
      return {
        title: 'No Models Selected',
        subtitle: 'Select at least one model from the filters to view data',
      };
    }
    if (isNoData) {
      return {
        title: 'No Data Available',
        subtitle: 'No energy data found for the selected period and filters',
      };
    }
    return {
      title: `No ${groupLabel} Selected`,
      subtitle: `Select a ${groupLabel.toLowerCase()} from the dropdown to get started`,
    };
  };

  if (showEmptyMessage) {
    const message = getEmptyMessage();
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <div className="text-muted-foreground">
            {noModelsSelected ? (
              <Truck className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            ) : groupLabel === 'Production' ? (
              <Film className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            ) : (
              <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            )}
            <p className="text-lg font-medium mb-1 text-center">
              {message.title}
            </p>
            <p className="text-base text-muted-foreground/70 text-center">
              {message.subtitle}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* KPI Row - 4 cards with icon strip matching SummaryReportView */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { Icon: Sun, bgColor: '#f59e0b' },      // Total Solar - amber
          { Icon: Unplug, bgColor: '#8FB560' },   // Off-Grid - green
          { Icon: Leaf, bgColor: '#10b981' },     // CO₂ Reduced - emerald
          { Icon: DollarSign, bgColor: '#8FB560' }, // Cost Saved - green
        ].map(({ Icon, bgColor }, i) => (
          <Card key={i} className="overflow-hidden">
            <CardContent className="p-0">
              <div className="flex items-stretch min-h-[72px]">
                {/* Icon strip */}
                <div
                  className="flex items-center justify-center w-14 shrink-0"
                  style={{ backgroundColor: `${bgColor}15` }}
                >
                  <Icon className="h-6 w-6 opacity-30" style={{ color: bgColor }} />
                </div>
                {/* Content area */}
                <div className="flex-1 p-2 flex flex-col justify-center min-w-0">
                  <Skeleton className="h-2.5 w-16 mb-1" />
                  <Skeleton className="h-5 w-20 mx-auto" />
                  <Skeleton className="h-2.5 w-14 mx-auto mt-1" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Performance highlights & energy mix - 2 columns */}
      <div className="grid lg:grid-cols-2 gap-3">
        {/* Performance Highlights */}
        <Card className="h-full">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-[#8FB560] opacity-30" />
              <span className="opacity-50">Performance Highlights</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex flex-col rounded-md bg-muted/40 p-3 gap-1">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-5 w-16" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Energy Mix & Financials */}
        <Card className="h-full">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
              <Zap className="h-4 w-4 text-[#8FB560] opacity-30" />
              <span className="opacity-50">Energy Mix & Financials</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex flex-col rounded-md bg-muted/40 p-3 gap-1">
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-5 w-20" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Energy Totals card - Zap icon, 3x3 grid */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
            <Zap className="h-4 w-4 text-[#8FB560] opacity-30" />
            <span className="opacity-50">Energy Totals</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-x-6 gap-y-2">
            {/* Row 1 */}
            {[...Array(3)].map((_, i) => (
              <div key={`r1-${i}`} className="flex justify-between py-1.5 border-b border-border/50">
                <Skeleton className="h-4 w-14" />
                <Skeleton className="h-4 w-16" />
              </div>
            ))}
            {/* Row 2 */}
            {[...Array(3)].map((_, i) => (
              <div key={`r2-${i}`} className="flex justify-between py-1.5 border-b border-border/50">
                <Skeleton className="h-4 w-14" />
                <Skeleton className="h-4 w-16" />
              </div>
            ))}
            {/* Row 3 - no border */}
            {[...Array(3)].map((_, i) => (
              <div key={`r3-${i}`} className="flex justify-between py-1.5">
                <Skeleton className="h-4 w-14" />
                <Skeleton className="h-4 w-16" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Sustainability Impact card - centered layout */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
            <Leaf className="h-4 w-4 text-[#8FB560] opacity-30" />
            <span className="opacity-50">Sustainability Impact</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { bgColor: 'bg-emerald-500/10' },
              { bgColor: 'bg-amber-500/10' },
              { bgColor: 'bg-[#8FB560]/10' },
            ].map(({ bgColor }, i) => (
              <div key={i} className={cn("flex flex-col items-center text-center gap-2 p-3 rounded-lg min-h-[104px]", bgColor)}>
                <Skeleton className="h-6 w-6 rounded" />
                <Skeleton className="h-5 w-12" />
                <Skeleton className="h-3 w-20" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* 2-Column Table - Financial | Operations */}
      <Card>
        <CardContent className="p-0">
          <div className="grid grid-cols-2 divide-x divide-border">
            {/* Financial Column */}
            <div>
              <div className="px-4 py-2 bg-muted/30 text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5 border-b border-border">
                <DollarSign className="h-3.5 w-3.5 text-[#8FB560] opacity-30" />
                <span className="opacity-50">Financial</span>
              </div>
              <div className="divide-y divide-border/50">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="px-4 py-2 flex justify-between items-center">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-4 w-16" />
                  </div>
                ))}
              </div>
            </div>

            {/* Operations Column */}
            <div>
              <div className="px-4 py-2 bg-muted/30 text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5 border-b border-border">
                <Cog className="h-3.5 w-3.5 text-[#8FB560] opacity-30" />
                <span className="opacity-50">Operations</span>
              </div>
              <div className="divide-y divide-border/50">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="px-4 py-2 flex justify-between items-center">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-14" />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Battery & Generator row */}
          <div className="border-t border-border">
            <div className="px-4 py-2 bg-muted/30 text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
              <Battery className="h-3.5 w-3.5 text-[#8FB560] opacity-30" />
              <span className="opacity-50">Battery & Generator</span>
            </div>
            <div className="grid grid-cols-2 gap-y-1 sm:grid-cols-4 sm:gap-y-0 sm:divide-x sm:divide-border/50">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="px-4 py-2 text-center">
                  <Skeleton className="h-3 w-20 mx-auto mb-1" />
                  <Skeleton className="h-4 w-16 mx-auto" />
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function GroupAnalyticsPanel({
  groups,
  filters,
  showCharts = true,
  showStats = true,
  dateRange,
  selectedCompany,
  groupLabel = 'Production',
  isFilmIndustry = false,
  stationMetricsData: propsStationMetricsData,
  isLoadingMetrics: propsIsLoadingMetrics,
  unitCount,
  isContextLoading = false,
  className,
  noModelsSelected = false,
  deployments = [],
  showDeploymentAnnotations = false,
  unitIds = null,
}: GroupAnalyticsPanelProps) {
  const isMobile = useIsMobile();
  const [sitesExpanded, setSitesExpanded] = useState(false);

  // Get selected group IDs from filters
  const selectedGroupIds = filters.groupIds;

  // Get selected group names for badge display
  // Include parent groups when their children (sites) are selected
  const selectedGroups = useMemo(() => {
    if (selectedGroupIds.length === 0) return [];

    const allParentGroups = groups.filter(g => !g.parent_group_id);
    const selectedGroups = groups.filter(g => selectedGroupIds.includes(g.id));

    // Get directly selected parent groups
    const directlySelectedParents = allParentGroups.filter(g => selectedGroupIds.includes(g.id));

    // Get parent groups whose children are selected (even if parent itself isn't selected)
    const parentIdsFromChildren = new Set<string>();
    selectedGroups.forEach(g => {
      if (g.parent_group_id) {
        parentIdsFromChildren.add(g.parent_group_id);
      }
    });

    // Combine: directly selected parents + parents with selected children
    const allRelevantParentIds = new Set([
      ...directlySelectedParents.map(g => g.id),
      ...parentIdsFromChildren,
    ]);

    return allParentGroups.filter(g => allRelevantParentIds.has(g.id));
  }, [selectedGroupIds, groups]);

  // Build display title based on selected groups
  // Only show detailed "Group · Site1 · Site2" format when ONE group is involved
  // Multiple groups show "X Productions Selected"
  // Returns string, or object with parentName + children for detailed display
  type DisplayTitleResult = string | { parentName: string; children: Group[] };
  const displayTitle = useMemo((): DisplayTitleResult => {
    if (selectedGroupIds.length === 0) {
      return `No ${groupLabel.charAt(0).toUpperCase() + groupLabel.slice(1)} Selected`;
    }

    // Get all selected groups with their details
    const selectedGroups = groups.filter(g => selectedGroupIds.includes(g.id));

    if (selectedGroups.length === 0) {
      return `No ${groupLabel.charAt(0).toUpperCase() + groupLabel.slice(1)} Selected`;
    }

    // Separate parent groups (roots) and child groups (sites)
    const parentGroups = selectedGroups.filter(g => !g.parent_group_id);
    const childGroups = selectedGroups.filter(g => g.parent_group_id);

    // Determine how many unique parent groups are involved
    const parentIdsFromChildren = new Set(childGroups.map(c => c.parent_group_id));
    const allInvolvedParentIds = new Set([
      ...parentGroups.map(g => g.id),
      ...parentIdsFromChildren,
    ]);

    // If more than one parent group is involved, show count
    // Use industry-specific label: "Production" for film, "Group" for others
    if (allInvolvedParentIds.size > 1) {
      const effectiveLabel = isFilmIndustry ? 'Production' : 'Group';
      return `${allInvolvedParentIds.size} ${effectiveLabel}s Selected`;
    }

    // Single parent group involved - show detailed title
    // Get the single parent (either directly selected or via child)
    let singleParent: Group | undefined;
    if (parentGroups.length === 1) {
      singleParent = parentGroups[0];
    } else if (parentIdsFromChildren.size === 1) {
      const parentId = [...parentIdsFromChildren][0];
      singleParent = groups.find(g => g.id === parentId);
    }

    if (!singleParent) {
      return `${selectedGroups.length} ${groupLabel}${selectedGroups.length > 1 ? 's' : ''} Selected`;
    }

    // If only parent is selected (no children explicitly selected)
    if (parentGroups.length === 1 && childGroups.length === 0) {
      return singleParent.name;
    }

    // If parent and/or children selected - return object with parent and children for flexible rendering
    const childrenOfParent = childGroups.filter(c => c.parent_group_id === singleParent!.id);
    if (childrenOfParent.length > 0) {
      return {
        parentName: singleParent.name,
        children: childrenOfParent,
      };
    }

    return singleParent.name;
  }, [selectedGroupIds, groups, groupLabel, isFilmIndustry]);

  // Get selected industry types from global filter context
  // This ensures that when MED is selected under Film, only Film MED units are included
  const { selectedIndustryTypes } = useFilters();

  // Fetch REAL station metrics from Supabase for the table
  // Skip the hook if data is passed from parent (to avoid duplicate API calls)
  const {
    data: hookStationMetricsData,
    isLoading: hookIsLoadingMetrics,
    error: metricsError,
    setPage: setMetricsPage,
    setPageSize: setMetricsPageSize,
    refetch: refetchMetrics,
  } = useStationMetrics({
    groupIds: selectedGroupIds,
    dateRange: dateRange || { start: null, end: null },
    modelCodes: filters.modelCodes,
    industryTypes: selectedIndustryTypes, // Filter by selected industries
    unitIds,
    enabled: selectedGroupIds.length > 0 && !propsStationMetricsData,
  });

  // Use props data if available, otherwise use hook data
  const stationMetricsData = propsStationMetricsData ?? hookStationMetricsData;
  const isLoadingMetrics = propsIsLoadingMetrics ?? hookIsLoadingMetrics;

  // Show toast notification when station metrics API fails
  useEffect(() => {
    if (metricsError) {
      toast.error('Failed to load station metrics. Please try again.');
    }
  }, [metricsError]);

  // Fetch REAL time series data from Supabase for charts
  const {
    timeSeries: realTimeSeries,
    isLoading: isLoadingTimeSeries,
  } = useEnergyTimeSeries({
    groupIds: selectedGroupIds,
    modelCodes: filters.modelCodes,
    industryTypes: selectedIndustryTypes,
    unitIds,
    startDate: dateRange?.start,
    endDate: dateRange?.end,
    granularity: filters.granularity,
    enabled: selectedGroupIds.length > 0,
  });

  // Fetch effective rate for the first selected group (for rate display)
  // When multiple groups are selected, we use the first one's rate as representative
  const firstGroupId = selectedGroupIds.length > 0 ? selectedGroupIds[0] : undefined;
  const { rate: groupEffectiveRate } = useGroupEffectiveRate({
    groupId: firstGroupId,
    autoFetch: selectedGroupIds.length > 0,
  });

  // Build analytics data from REAL database values (no mock data)
  const analyticsData = useMemo<GroupAnalyticsData | null>(() => {
    if (selectedGroupIds.length === 0) {
      return null;
    }

    // Fill in missing periods with 0 values for the complete date range
    // This ensures the chart shows the full group period, not just dates with data
    const filledTimeSeries = fillMissingPeriods(
      realTimeSeries,
      dateRange?.start || null,
      dateRange?.end || null,
      filters.granularity
    );
    const timeSeries = filledTimeSeries;

    // ============================================================================
    // TIME SERIES is the single source of truth for energy flow metrics
    // These values come from get_energy_time_series RPC which calculates them
    // with correct rates and aggregation at the database level
    // ============================================================================
    const totalSolarKwh = timeSeries.reduce((sum, p) => sum + p.solarKwh, 0);
    const totalGridKwh = timeSeries.reduce((sum, p) => sum + p.gridKwh, 0);
    const totalGridExportKwh = timeSeries.reduce((sum, p) => sum + p.gridExportKwh, 0);
    const totalGeneratorKwh = timeSeries.reduce((sum, p) => sum + p.generatorKwh, 0);
    const totalCO2 = timeSeries.reduce((sum, p) => sum + p.co2Reduced, 0);
    const totalCostSaved = timeSeries.reduce((sum, p) => sum + p.costSaved, 0);
    // gridSellRevenue is calculated at DB level with effective rate (default $0.08/kWh)
    const totalGridSellRevenue = timeSeries.reduce((sum, p) => sum + p.gridSellRevenue, 0);
    // Energy-weighted range share from the measured kWh totals (generator
    // included — the RPC's offGridPercent field is generator-blind).
    // Weighting by volume keeps a 1 kWh day from counting like a
    // 1,000 kWh day; synthetic fill rows contribute zero to every sum.
    const totalEnergyKwh = totalSolarKwh + totalGridKwh + totalGeneratorKwh;
    const avgOffGrid = totalEnergyKwh > 0
      ? Math.round((totalSolarKwh / totalEnergyKwh) * 100)
      : 0;

    // Calculate days in period from date range or time series
    let daysInPeriod = 30;
    if (dateRange?.start && dateRange?.end) {
      daysInPeriod = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    } else if (timeSeries.length > 0) {
      const periodDays = filters.granularity === 'daily' ? 1 : filters.granularity === 'weekly' ? 7 : 30;
      daysInPeriod = timeSeries.length * periodDays;
    }

    const dailyAvgSolarKwh = daysInPeriod > 0 ? Math.round((totalSolarKwh / daysInPeriod) * 10) / 10 : 0;

    // Find best/worst periods
    const sortedByOffGrid = [...timeSeries].sort((a, b) => b.offGridPercent - a.offGridPercent);
    const bestPeriod = sortedByOffGrid[0]
      ? { label: sortedByOffGrid[0].periodLabel, value: sortedByOffGrid[0].offGridPercent, metric: 'Off-Grid %' }
      : { label: '-', value: 0, metric: 'Off-Grid %' };
    const worstPeriod = sortedByOffGrid[sortedByOffGrid.length - 1]
      ? { label: sortedByOffGrid[sortedByOffGrid.length - 1].periodLabel, value: sortedByOffGrid[sortedByOffGrid.length - 1].offGridPercent, metric: 'Off-Grid %' }
      : { label: '-', value: 0, metric: 'Off-Grid %' };

    // Calculate trend
    const firstPeriod = timeSeries[0];
    const lastPeriod = timeSeries[timeSeries.length - 1];
    const trendVsPrior = firstPeriod && lastPeriod && firstPeriod.offGridPercent > 0
      ? Math.round(((lastPeriod.offGridPercent - firstPeriod.offGridPercent) / firstPeriod.offGridPercent) * 1000) / 10
      : 0;

    // ============================================================================
    // STATION METRICS only used for values NOT available in timeSeries:
    // - loadKwh, batteryChargeKwh, batteryDischargeKwh, avgBatterySoc
    // Energy flow metrics (solar, grid, export, CO2, cost, revenue) come from
    // timeSeries above - this ensures one source of truth
    // ============================================================================
    let batteryChargeKwh = 0;
    let batteryDischargeKwh = 0;
    let loadKwh = 0;

    if (stationMetricsData?.totals) {
      const realTotals = stationMetricsData.totals;
      // Only use stationMetrics for battery/load data (not in timeSeries)
      batteryChargeKwh = realTotals.batteryChargeKwh;
      batteryDischargeKwh = realTotals.batteryDischargeKwh;
      loadKwh = realTotals.loadKwh;
    }

    // Use timeSeries totals as the single source of truth
    const finalTotalSolarKwh = totalSolarKwh;
    const finalTotalCO2 = totalCO2;
    const finalTotalCostSaved = totalCostSaved;
    const gridImportKwh = totalGridKwh;
    const gridExportKwh = totalGridExportKwh;
    const generatorKwh = totalGeneratorKwh;
    const gridSellRevenue = totalGridSellRevenue;
    const totalProfit = finalTotalCostSaved + gridSellRevenue;

    // Calculate energy mix (Solar → Battery → Load is one path, so battery not shown separately)
    // Only show: Solar (primary), Grid (backup <3%), Generator (rare backup <1%)
    const totalEnergy = finalTotalSolarKwh + gridImportKwh + generatorKwh;
    const energyMix = totalEnergy > 0 ? {
      solarKwh: finalTotalSolarKwh,
      gridKwh: gridImportKwh,
      generatorKwh,
      solarPercent: Math.round((finalTotalSolarKwh / totalEnergy) * 100),
      gridPercent: Math.round((gridImportKwh / totalEnergy) * 100),
      generatorPercent: Math.round((generatorKwh / totalEnergy) * 100),
    } : {
      solarKwh: 0, gridKwh: 0, generatorKwh: 0,
      solarPercent: 0, gridPercent: 0, generatorPercent: 0,
    };

    return {
      groupName: typeof displayTitle === 'string' ? displayTitle : displayTitle.parentName,
      groupId: selectedGroupIds.length === 1 ? selectedGroupIds[0] : 'multiple',
      dateRange: {
        start: dateRange?.start?.toISOString() || new Date().toISOString(),
        end: dateRange?.end?.toISOString() || new Date().toISOString(),
      },
      granularity: filters.granularity,
      timeSeries,
      stats: {
        // The RPC's measured off-grid average IS the metric — deriving it
        // from solar/load miscounts exported, stored, or generator energy.
        avgOffGrid,
        // No measured range peak exists (dailyAvg/8h is an invented
        // coefficient) — prod renders N/A until the metrics carry one;
        // demo keeps its simulated estimate.
        peakSolarKw: currentSupabaseEnv === 'demo'
          ? Math.round((dailyAvgSolarKwh / 8) * 10) / 10
          : null,
        totalCO2: finalTotalCO2,
        totalCostSaved: finalTotalCostSaved,
        totalSolarKwh: finalTotalSolarKwh,
        totalGridKwh: gridImportKwh,
        bestPeriod,
        worstPeriod,
        trendVsPrior,
        // Estimated equivalences from MEASURED kWh, via the documented
        // shared constants (labeled with ≈ in the UI). "Gen hours saved"
        // has no measured runtime source — N/A on prod; demo keeps the
        // /8 estimate.
        dieselGallonsReduced: Math.round(finalTotalSolarKwh / ENERGY_CONVERSION.DIESEL_EFFECTIVE_KWH_PER_GALLON),
        treesEquivalent: Math.round(finalTotalCO2 / ENERGY_CONVERSION.KG_CO2_PER_TREE_PER_YEAR),
        genHoursReduced: currentSupabaseEnv === 'demo' ? Math.round(finalTotalSolarKwh / 8) : null,
        dailyAvgSolarKwh: daysInPeriod > 0 ? Math.round((finalTotalSolarKwh / daysInPeriod) * 10) / 10 : 0,
        daysInPeriod,
        // One first-vs-last off-grid trend is NOT three per-metric prior
        // period deltas — prod hides the chips until a real prior-period
        // fetch exists; demo keeps its simulated chips.
        priorPeriodComparison: currentSupabaseEnv === 'demo'
          ? { solarKwhDelta: trendVsPrior, offGridDelta: trendVsPrior, co2Delta: trendVsPrior }
          : { solarKwhDelta: null, offGridDelta: null, co2Delta: null },
        totalLoadKwh: loadKwh,
        totalBatteryChargeKwh: batteryChargeKwh,
        totalBatteryDischargeKwh: batteryDischargeKwh,
        totalGridImportKwh: gridImportKwh,
        totalGridExportKwh: gridExportKwh,
        totalGeneratorKwh: generatorKwh,
        totalCo2EmissionKg: finalTotalCO2,
        avgBatterySoc: stationMetricsData?.totals?.avgBatterySoc ?? 0,
        gridSellRevenue,
        totalProfit,
      },
      energyMix,
      unitsInGroup: unitCount ?? stationMetricsData?.totalUnits ?? 0,
      // Include effective rate info if available
      effectiveRate: groupEffectiveRate ? {
        rateName: groupEffectiveRate.rateName,
        gridImportRate: groupEffectiveRate.gridImportRate,
        gridSellRate: groupEffectiveRate.gridSellRate,
        dieselCostPerGallon: groupEffectiveRate.dieselCostPerGallon ?? ENERGY_RATES.DIESEL_COST_PER_GALLON,
        utilityProvider: groupEffectiveRate.utilityProvider ?? undefined,
        nemVersion: groupEffectiveRate.nemVersion ?? undefined,
        rateSource: groupEffectiveRate.rateSource,
        sourceName: groupEffectiveRate.sourceName ?? 'Unknown',
      } : undefined,
    };
  }, [selectedGroupIds, realTimeSeries, dateRange, filters.granularity, stationMetricsData?.totals, stationMetricsData?.totalUnits, displayTitle, unitCount, groupEffectiveRate]);

  const hasData = analyticsData !== null && analyticsData.timeSeries.length > 0;
  // Always show content when we have valid date range and groups selected (even if no energy data)
  const hasValidDateRange = dateRange?.start && dateRange?.end;
  const hasGroupsSelected = selectedGroupIds.length > 0;
  // Show analytics panel (with 0 values) when groups are selected, we have a valid date range, AND analyticsData is ready
  // BUT hide analytics when no models are selected (show empty state instead)
  const showAnalytics = hasGroupsSelected && hasValidDateRange && analyticsData !== null && !noModelsSelected;
  const viewMode = filters.viewMode || 'charts';
  const viewLabel = viewMode === 'summary' ? 'SUMMARY' : viewMode === 'charts' ? 'CHARTS' : 'COMPARE';

  // Determine if we're in a "no data" state (groups selected, not loading, but no time series data)
  // Also treat "no models selected" as a no-data state
  const isNoDataState = (selectedGroupIds.length > 0 && !isLoadingTimeSeries && realTimeSeries.length === 0) || noModelsSelected;
  // Show empty state when NO groups selected OR when no models selected
  const showEmptyState = selectedGroupIds.length === 0 || noModelsSelected;

  // Show header skeleton if context is loading OR if selected groups can't be resolved yet
  // This prevents text flicker when groups array isn't populated but selectedGroupIds has values
  const showHeaderSkeleton = isContextLoading ||
    (selectedGroupIds.length > 0 && groups.filter(g => selectedGroupIds.includes(g.id)).length === 0);

  return (
    <div className={cn("space-y-4", className)}>
      {/* Header with View Toggle */}
      <div className="space-y-3">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg md:text-xl font-semibold flex items-start gap-2">
              {showHeaderSkeleton ? (
                <>
                  <Skeleton className="h-5 w-5 rounded shrink-0 mt-0.5" />
                  {/* h-7 matches text-lg/text-xl line-height (28px) */}
                  <Skeleton className="h-7 w-48" />
                </>
              ) : (
                <>
                  {groupLabel === 'Production' ? (
                    <Film className="h-5 w-5 text-[#8FB560] shrink-0 mt-0.5" />
                  ) : (
                    <Building2 className="h-5 w-5 text-[#8FB560] shrink-0 mt-0.5" />
                  )}
                  {typeof displayTitle === 'string' ? (
                    <span>{displayTitle}</span>
                  ) : (
                    <span className="flex flex-wrap items-baseline w-full">
                      {/* Parent name - inline with children on desktop */}
                      <span>{displayTitle.parentName}</span>
                      {/* Mobile toggle button */}
                      {isMobile && displayTitle.children.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setSitesExpanded(!sitesExpanded)}
                          className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors ml-auto"
                          aria-label={sitesExpanded ? 'Hide sites' : 'Show sites'}
                        >
                          <span className="px-1.5 py-0.5 text-xs rounded-full bg-[#8FB560] text-black font-medium">
                            {displayTitle.children.length}
                          </span>
                          {sitesExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                      )}
                      {/* Sites - inline on desktop, collapsible on mobile */}
                      {((!isMobile) || sitesExpanded) && displayTitle.children.map((child) => (
                        <span key={child.id} className="flex items-baseline text-sm md:text-base font-normal">
                          <span className="text-muted-foreground mx-1">·</span>
                          <span className="text-[#8FB560]">{child.name}</span>
                        </span>
                      ))}
                    </span>
                  )}
                </>
              )}
            </h2>

            {/* Date range and unit count - always show when groups selected */}
            <div className="mt-1">
              {showAnalytics ? (
                <div className="text-sm text-muted-foreground flex flex-col md:flex-row md:items-center gap-0.5 md:gap-1.5">
                  <span className="flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5 shrink-0" />
                    {formatDateRange(analyticsData.dateRange.start, analyticsData.dateRange.end, analyticsData.stats.daysInPeriod)}
                  </span>
                  {/* Unit count from units data (same source as dropdowns) - bypasses broken RPC */}
                  {(unitCount ?? 0) > 0 && (
                    <span className="flex items-center gap-1">
                      <span className="hidden md:inline">·</span>
                      <Truck className="h-3.5 w-3.5 shrink-0" />
                      {unitCount} {unitCount === 1 ? 'Unit' : 'Units'}
                    </span>
                  )}
                </div>
              ) : (
                /* Skeleton matches populated date line structure for zero layout shift */
                /* h-5 matches text-sm line-height (20px) for proper alignment */
                <div className="text-sm flex flex-col md:flex-row md:items-center gap-0.5 md:gap-1.5">
                  <span className="flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5 shrink-0 text-muted-foreground/30" />
                    <Skeleton className="h-5 w-44" />
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="hidden md:inline text-muted-foreground/30">·</span>
                    <Truck className="h-3.5 w-3.5 shrink-0 text-muted-foreground/30" />
                    <Skeleton className="h-5 w-14" />
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* View label + Company Logo - top right (hidden on mobile) */}
          <div className="shrink-0 hidden md:flex items-center gap-8">
            <span className="text-lg font-semibold tracking-[0.28em] text-white">
              {viewLabel}
            </span>
            <img
              src={selectedCompany?.logo_url || DEFAULT_LOGO}
              alt={selectedCompany?.name || 'Hollywood Trucks'}
              className="h-10 w-auto object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).src = DEFAULT_LOGO;
              }}
            />
          </div>
        </div>

        {/* Production badges area - full width row below header */}
        <div className="min-h-[38px]">
          {selectedGroups.length > 0 ? (
            <GroupBadgeList groups={selectedGroups} />
          ) : (
            <div className="flex gap-2">
              <Skeleton className="h-[30px] w-32 rounded-md" />
              <Skeleton className="h-[30px] w-28 rounded-md" />
            </div>
          )}
        </div>
      </div>

      {/* Summary View */}
      {viewMode === 'summary' && (
        showAnalytics ? (
          <SummaryReportView data={analyticsData} />
        ) : (
          <SummarySkeleton showEmptyMessage={showEmptyState} groupLabel={groupLabel} isNoData={isNoDataState} noModelsSelected={noModelsSelected} />
        )
      )}

      {/* Charts View - Always render layout to prevent shift */}
      {viewMode === 'charts' && (
        <>
          {/* KPI Cards - Show data (with 0 values if no energy data) when groups selected */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {showAnalytics ? (
              <>
                <KpiCard
                  icon={Sun}
                  label="Total Solar"
                  value={analyticsData.stats.totalSolarKwh >= 1_000_000
                    ? `${(analyticsData.stats.totalSolarKwh / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} GWh`
                    : `${Math.round(analyticsData.stats.totalSolarKwh).toLocaleString()} kWh`
                  }
                  delta={hasData ? analyticsData.stats.priorPeriodComparison.solarKwhDelta ?? undefined : undefined}
                  iconColor="#f59e0b"
                  subtitle={`${analyticsData.stats.dailyAvgSolarKwh.toLocaleString()} kWh/day avg`}
                />
                <KpiCard
                  icon={Unplug}
                  label="Off-Grid"
                  value={`${analyticsData.stats.avgOffGrid}%`}
                  delta={hasData ? analyticsData.stats.priorPeriodComparison.offGridDelta ?? undefined : undefined}
                  iconColor="#8FB560"
                  subtitle={hasData ? `Peak: ${analyticsData.stats.bestPeriod.value}%` : 'No data for period'}
                />
                <KpiCard
                  icon={Leaf}
                  label="CO₂ Reduced"
                  value={`${Math.round(analyticsData.stats.totalCO2).toLocaleString()} kg`}
                  delta={hasData ? analyticsData.stats.priorPeriodComparison.co2Delta ?? undefined : undefined}
                  iconColor="#10b981"
                  subtitle={`≈ ${analyticsData.stats.treesEquivalent.toLocaleString()} trees`}
                />
                <KpiCard
                  icon={DollarSign}
                  label="Cost Saved"
                  value={`$${analyticsData.stats.totalCostSaved.toLocaleString()}`}
                  iconColor="#8FB560"
                  subtitle={`${analyticsData.stats.dieselGallonsReduced.toLocaleString()} gal diesel`}
                />
              </>
            ) : (
              <>
                <KpiCardSkeleton icon={Sun} iconColor="#f59e0b" />
                <KpiCardSkeleton icon={Leaf} iconColor="#8FB560" />
                <KpiCardSkeleton icon={Leaf} iconColor="#10b981" />
                <KpiCardSkeleton icon={DollarSign} iconColor="#8FB560" />
              </>
            )}
          </div>

          {/* Main Chart - Show chart (empty if no data) when groups selected */}
          {showCharts && (
            showAnalytics ? (
              <GroupBarChart
                data={analyticsData.timeSeries}
                enabledMetrics={filters.enabledMetrics}
                title={`${groupLabel} Metrics Over Time`}
                deployments={deployments}
                showDeploymentAnnotations={showDeploymentAnnotations}
                dateRange={dateRange}
              />
            ) : (
              <ChartSkeleton showEmptyMessage={showEmptyState} groupLabel={groupLabel} isNoData={isNoDataState} noModelsSelected={noModelsSelected} />
            )
          )}

          {/* Bottom Row: Stats + Energy Mix */}
          {showStats && (
            <div className="grid md:grid-cols-3 gap-3 min-h-[340px]">
              {showAnalytics ? (
                <>
                  <StatsSummaryCard
                    stats={analyticsData.stats}
                    title="Sustainability Summary"
                  />
                  <GridRevenueCard
                    stats={analyticsData.stats}
                    title="Grid Revenue"
                    gridSellRate={analyticsData.effectiveRate?.gridSellRate}
                    gridImportRate={analyticsData.effectiveRate?.gridImportRate}
                  />
                  <EnergyMixDonut
                    energyMix={analyticsData.energyMix}
                    title="Energy Source Mix"
                  />
                </>
              ) : (
                <>
                  <StatsCardSkeleton />
                  <GridRevenueCardSkeleton />
                  <DonutSkeleton />
                </>
              )}
            </div>
          )}

          {/* Station Metrics Table - Uses REAL data from Supabase */}
          {/* Show skeleton when no models selected but groups are selected */}
          {noModelsSelected && hasGroupsSelected && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-[#8FB560] opacity-30" />
                  Station Energy Metrics
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    {/* Table header skeleton */}
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th className="px-3 py-2 text-left w-8"><Skeleton className="h-3.5 w-4" /></th>
                        <th className="px-3 py-2 text-left"><Skeleton className="h-3.5 w-16" /></th>
                        <th className="px-3 py-2 text-right"><Skeleton className="h-3.5 w-10 ml-auto" /></th>
                        <th className="px-3 py-2 text-right"><Skeleton className="h-3.5 w-8 ml-auto" /></th>
                        <th className="px-3 py-2 text-right"><Skeleton className="h-3.5 w-12 ml-auto" /></th>
                        <th className="px-3 py-2 text-right"><Skeleton className="h-3.5 w-14 ml-auto" /></th>
                        <th className="px-3 py-2 text-right"><Skeleton className="h-3.5 w-8 ml-auto" /></th>
                        <th className="px-3 py-2 text-right"><Skeleton className="h-3.5 w-12 ml-auto" /></th>
                        <th className="px-3 py-2 text-right"><Skeleton className="h-3.5 w-14 ml-auto" /></th>
                      </tr>
                    </thead>
                    {/* Table row skeletons - 4 rows */}
                    <tbody>
                      {[1, 2, 3, 4].map((i) => (
                        <tr key={i} className="border-b border-border/50">
                          <td className="px-3 py-2 w-8"><Skeleton className="h-3.5 w-4" /></td>
                          <td className="px-3 py-2"><Skeleton className="h-3.5 w-20" /></td>
                          <td className="px-3 py-2 text-right"><Skeleton className="h-3.5 w-12 ml-auto" /></td>
                          <td className="px-3 py-2 text-right"><Skeleton className="h-3.5 w-8 ml-auto" /></td>
                          <td className="px-3 py-2 text-right"><Skeleton className="h-3.5 w-10 ml-auto" /></td>
                          <td className="px-3 py-2 text-right"><Skeleton className="h-3.5 w-10 ml-auto" /></td>
                          <td className="px-3 py-2 text-right"><Skeleton className="h-3.5 w-10 ml-auto" /></td>
                          <td className="px-3 py-2 text-right"><Skeleton className="h-3.5 w-10 ml-auto" /></td>
                          <td className="px-3 py-2 text-right"><Skeleton className="h-3.5 w-8 ml-auto" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Pagination skeleton */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <Skeleton className="h-3.5 w-32" />
                  <div className="flex items-center gap-1">
                    <Skeleton className="h-7 w-7 rounded" />
                    <Skeleton className="h-7 w-7 rounded" />
                    <Skeleton className="h-7 w-7 rounded" />
                    <Skeleton className="h-7 w-7 rounded" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          {hasData && !noModelsSelected && (stationMetricsData || isLoadingMetrics || metricsError) && (
            isLoadingMetrics ? (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-[#8FB560] opacity-30" />
                    <Skeleton className="h-3 w-40" />
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-8 w-full" />
                  ))}
                </CardContent>
              </Card>
            ) : metricsError ? (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-red-500" />
                    Station Metrics
                  </CardTitle>
                </CardHeader>
                <CardContent className="py-8 text-center">
                  <p className="text-red-400 mb-4">Failed to load station metrics</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={refetchMetrics}
                  >
                    Retry
                  </Button>
                </CardContent>
              </Card>
            ) : stationMetricsData ? (
              <StationMetricsTable
                data={stationMetricsData}
                onPageChange={setMetricsPage}
                onPageSizeChange={setMetricsPageSize}
              />
            ) : null
          )}
        </>
      )}
    </div>
  );
}
